/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  deleteNotifications,
  markNotificationsAsUnread,
  readNotifications,
  UUID,
} from '@builddocs/data-management'
import { useToast } from '@builddocs/system'
import {
  Checkbox,
  FormControlLabel,
  List,
  Stack,
  Typography,
} from '@mui/material'
import * as Sentry from '@sentry/react'
import _ from 'lodash'

import NotificationItem from '../molecules/NotificationItem'
import NotificationTools from '../molecules/NotificationTools'

import { NotificationListSkeleton } from './NotificationListSkeleton'

import type { Notification } from '@builddocs/data-management'

interface OwnProps {
  notifications: Notification[] | null | undefined
  isLoading: boolean
  lastPage: boolean
  setPageNumber: Dispatch<SetStateAction<number>>
  onClosePopover: () => void
  refetch: (unread?: boolean | null) => void
  onlyUnread: boolean | null
}

type NotificationsItemsListProps = OwnProps

export default function NotificationList(props: NotificationsItemsListProps) {
  const {
    notifications,
    onClosePopover,
    refetch,
    isLoading,
    onlyUnread,
    lastPage,
    setPageNumber,
    ...rest
  } = props

  const { toast } = useToast()
  const [checkedNots, setCheckedNots] = useState<
    Record<UUID, { checked: boolean; viewed: boolean }>
  >({})

  useEffect(() => {
    if (notifications) {
      setCheckedNots(
        notifications.reduce(
          (acc, item) => ({
            ...acc,
            [item.notificationTargetId]: {
              checked: false,
              viewed: item.viewed,
            },
          }),
          {},
        ),
      )
    }
  }, [notifications])

  const checkedStates: { isAllSelected: boolean; isAllReading: boolean } =
    useMemo(
      () =>
        _.isEmpty(checkedNots)
          ? { isAllSelected: false, isAllReading: false }
          : {
              isAllSelected: !Object.values(checkedNots).some(
                item => !item.checked,
              ),
              isAllReading:
                Object.values(checkedNots).some(item => item.checked) &&
                !Object.values(checkedNots).some(
                  item => item.checked && !item.viewed,
                ),
            },
      [checkedNots],
    )

  const handOneSelect = (id: UUID) => {
    setCheckedNots(prev => ({
      ...prev,
      [id]: { ...prev[id], checked: !prev[id].checked },
    }))
  }

  const handAllSelect = () => {
    setCheckedNots(prev =>
      prev
        ? Object.keys(prev).reduce(
            (acc, id) => ({
              ...acc,
              [id]: { ...prev[id], checked: !checkedStates.isAllSelected },
            }),
            {},
          )
        : {},
    )
  }

  const handleReadCheckedNotifications = () => {
    const listIds: UUID[] = []

    checkedStates.isAllReading
      ? Object.keys(checkedNots).forEach(
          id => checkedNots[id].checked && listIds.push(id),
        )
      : Object.keys(checkedNots).forEach(
          id =>
            checkedNots[id].checked &&
            !checkedNots[id].viewed &&
            listIds.push(id),
        )
    ;(checkedStates.isAllReading
      ? markNotificationsAsUnread
      : readNotifications)(listIds)
      .then(() => refetch())
      .catch(error => {
        Sentry.captureException(error)
        toast.error(
          error?.response?.data?.message ??
            'Не удалось отметить как непрочитанные',
        )
      })
  }

  const handleDeleteCheckedNotifications = () => {
    const listIds: UUID[] = []

    Object.keys(checkedNots).forEach(
      id => checkedNots[id].checked && listIds.push(id),
    )

    if (lastPage && notifications?.length === listIds.length)
      setPageNumber(prevValue => prevValue - 1)

    deleteNotifications(listIds)
      .then(() => refetch())
      .catch(error => {
        Sentry.captureException(error)
        toast.error(
          error?.response?.data?.message ?? 'Не удалось удалить уведомления',
        )
      })
  }

  useEffect(() => {
    refetch()
  }, [])

  return (
    <Stack spacing={1}>
      {notifications && notifications?.length > 0 && (
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          spacing={1}
          sx={{ pl: 2, pr: 1 }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={checkedStates.isAllSelected}
                indeterminate={
                  !checkedStates.isAllSelected &&
                  Object.values(checkedNots).some(item => item.checked)
                }
                onChange={handAllSelect}
              />
            }
            disabled={isLoading}
            label="Выделить все"
            sx={{ py: 0, color: 'primary.main' }}
          />
          <NotificationTools
            disabled={
              !Object.values(checkedNots).some(item => item.checked) ||
              isLoading
            }
            opacity={1}
            title={{
              delete: 'Удалить выбранные',
              read: `Отметить выбранные как ${
                checkedStates.isAllReading ? 'непрочитанные' : 'прочитанные'
              }`,
            }}
            onDelete={handleDeleteCheckedNotifications}
            onRead={handleReadCheckedNotifications}
          />
        </Stack>
      )}
      {isLoading ? (
        <NotificationListSkeleton />
      ) : notifications && notifications.length > 0 ? (
        <List
          dense
          sx={{
            width: '100%',
            mt: 0,
            py: 0,
            '& li:hover + li hr:first-of-type, & li:focus + li hr:first-of-type':
              {
                opacity: 0,
              },
          }}
        >
          {notifications.map((notification, index) => {
            return (
              <Fragment key={notification.notificationTargetId}>
                <NotificationItem
                  handleChangePageNumber={() => {
                    if (notifications.length === 1)
                      setPageNumber(prevValue => prevValue - 1)
                  }}
                  last={notifications.length === index + 1}
                  notification={notification}
                  refetch={refetch}
                  onClosePopover={onClosePopover}
                  {...rest}
                  checked={
                    !!checkedNots[notification.notificationTargetId]?.checked
                  }
                  onSelect={() =>
                    handOneSelect(notification.notificationTargetId)
                  }
                />
              </Fragment>
            )
          })}
        </List>
      ) : (
        <Stack alignItems="center" sx={{ p: 2, width: '100%' }}>
          <Typography sx={{ fontWeight: 'bold' }}>Нет уведомлений</Typography>
        </Stack>
      )}
    </Stack>
  )
}
